// frontend/src/App.js
// ----------------------------------------------------
// WRZZAN Trading App - Main Application Component
// With Authentication & Modern UI Design
// ----------------------------------------------------

import React, { createContext, useEffect, useState, memo } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import axios from "axios";
import KiteLogin from "./components/KiteLogin";
import { io as ioClient } from "socket.io-client";
import PaperModePanel from "./components/PaperModePanel";
import LiveModePanel from "./components/LiveModePanel";
import StatusBadge from "./components/StatusBadge";
import useSessionWatcher from "./hooks/useSessionWatcher";
import MarketParamsSettings from "./components/MarketParamsSettings";

// Authentication imports
import { AuthProvider, useAuth } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";

// Import CSS
import "./index.css";
import { API_BASE } from "./config";

// Create and export a single socket instance so all components reuse it
const SOCKET_URL =
  process.env.REACT_APP_SOCKET_URL || window.location.origin.replace("3000", "8000");
export const socket = ioClient(SOCKET_URL, {
  transports: ["websocket", "polling"],
  reconnection: true,
  reconnectionAttempts: Infinity,
  reconnectionDelay: 2000,
});

// Optional: provide a React Context so components can use useContext(SocketContext)
export const SocketContext = createContext(socket);

// ----------------------------------------------------
// ✅ Session watcher wrapper
// ----------------------------------------------------
function SessionWatcherWrapper({ children }) {
  useSessionWatcher();
  return children;
}

// ----------------------------------------------------
// ✅ AutoRedirectHandler
// When Zerodha sends ?kite_login=success in the URL,
// we auto-redirect user to /kite for clean UI.
// ----------------------------------------------------
function AutoRedirectHandler() {
  const navigate = useNavigate();
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has("kite_login")) {
      navigate("/kite");
      window.history.replaceState({}, document.title, "/kite");
    }
  }, [navigate]);
  return null;
}

// ----------------------------------------------------
// ✅ Modern Navigation bar
// ----------------------------------------------------
function Nav({ onToggleSettings, settingsOpen }) {
  const { user, logout, isAuthenticated } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // Don't show nav on login/register pages
  if (!isAuthenticated) return null;

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="app-nav">
      <Link to="/live" className="nav-brand">
        <img src="/wrzzan-logo.png" alt="WRZZaN" className="brand-logo" />
        <span>WRZZaN</span>
      </Link>

      <div className="nav-links">
        <Link
          to="/kite"
          className={`nav-link ${isActive("/kite") ? "active" : ""}`}
        >
          <span>🔑</span>
          <span>Kite Login</span>
        </Link>
        <Link
          to="/paper"
          className={`nav-link ${isActive("/paper") ? "active" : ""}`}
        >
          <span>📄</span>
          <span>Paper Mode</span>
        </Link>
        <Link
          to="/live"
          className={`nav-link ${isActive("/live") ? "active" : ""}`}
        >
          <span>🔴</span>
          <span>Live Mode</span>
        </Link>

      </div>

      <div className="nav-right">
        <StatusBadge />
        <button
          onClick={onToggleSettings}
          className={`nav-settings-btn ${settingsOpen ? "active" : ""}`}
          title="Settings"
        >
          <span>⚙️</span>
        </button>
        {user && (
          <div className="nav-user">
            <div className="nav-user-avatar">
              {(user.name || user.email || "U").charAt(0).toUpperCase()}
            </div>
            <span>{user.name || user.email}</span>
          </div>
        )}
        <button onClick={handleLogout} className="nav-logout-btn">
          <span>🚪</span>
          <span>Logout</span>
        </button>
      </div>
    </nav>
  );
}

// ----------------------------------------------------
// ✅ Page Components
// ----------------------------------------------------
function PaperPage() {
  return (
    <div className="app-content">
      <div className="panel">
        <div className="panel-header">
          <h2 className="panel-title">
            <span className="icon">📄</span>
            Paper Mode Trading
          </h2>
        </div>
        <PaperModePanel />
      </div>
    </div>
  );
}

// 👇 wrap once here (so React doesn't remount on each LivePage render)
const StableLiveModePanel = memo(LiveModePanel);

function LivePage() {
  const [status, setStatus] = useState("");
  const [kiteConnected, setKiteConnected] = useState(null); // null = loading, true/false = known

  // Check Kite connection status on mount and periodically
  useEffect(() => {
    const checkKiteStatus = async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/strategy/status`);
        setKiteConnected(data?.kiteConnected === true);
      } catch (err) {
        console.warn("⚠️ Could not check Kite status:", err.message);
        setKiteConnected(false);
      }
    };

    checkKiteStatus();
    const interval = setInterval(checkKiteStatus, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = async () => {
    try {
      setStatus("🛑 Stopping live scheduler...");
      await axios.post(`${API_BASE}/strategy/stop`, { mode: "live" });

      setStatus("🧹 Scheduler stopped. Logging out from Zerodha...");
      await axios.get(`${API_BASE}/auth/logout`);

      setStatus("✅ Logged out successfully. Redirecting...");
      setKiteConnected(false);
      setTimeout(() => (window.location.href = "/kite"), 1200);
    } catch (err) {
      console.error("Logout failed:", err);
      setStatus("❌ Logout failed: " + (err.response?.data?.error || err.message));
    }
  };

  const handleLogin = () => {
    window.location.href = "/kite";
  };

  return (
    <div className="app-content">
      <div className="panel">
        <div className="panel-header">
          <h2 className="panel-title">
            <span className="icon">🔴</span>
            Live Mode (Zerodha)
          </h2>
          {/* Show Login button if not connected, Logout button if connected */}
          {kiteConnected === null ? (
            <button className="btn btn-outline" disabled>
              <span>⏳</span>
              Checking...
            </button>
          ) : kiteConnected ? (
            <button
              onClick={handleLogout}
              className="btn btn-danger"
            >
              <span>🔌</span>
              Logout Zerodha & Stop
            </button>
          ) : (
            <button
              onClick={handleLogin}
              className="btn btn-success"
            >
              <span>🔑</span>
              Login to Zerodha
            </button>
          )}
        </div>
        <StableLiveModePanel kiteConnected={kiteConnected} />
        {status && (
          <p style={{ marginTop: 16, color: "var(--color-text-secondary)" }}>
            {status}
          </p>
        )}
      </div>
    </div>
  );
}

function KitePage() {
  return (
    <div className="app-content">
      <div className="panel">
        <div className="panel-header">
          <h2 className="panel-title">
            <span className="icon">🔑</span>
            Zerodha Kite Login
          </h2>
        </div>
        <KiteLogin />
      </div>
    </div>
  );
}

// MonitorPage removed - not used

// ----------------------------------------------------
// ⚙️ Settings Sidebar Overlay
// ----------------------------------------------------
function SettingsSidebar({ open, onClose }) {
  return (
    <>
      {/* Backdrop */}
      <div
        className={`sidebar-backdrop ${open ? "visible" : ""}`}
        onClick={onClose}
      />
      {/* Sidebar Panel */}
      <div className={`sidebar-panel ${open ? "open" : ""}`}>
        <div className="sidebar-header">
          <h2 className="sidebar-title">
            <span>⚙️</span> Settings
          </h2>
          <button className="sidebar-close-btn" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="sidebar-body">
          <MarketParamsSettings />
        </div>
      </div>
    </>
  );
}

// ----------------------------------------------------
// ✅ Main App Component
// ----------------------------------------------------
function AppContent() {
  const [settingsOpen, setSettingsOpen] = useState(false);

  return (
    <div className="app-container">
      <Router>
        <SessionWatcherWrapper>
          <AutoRedirectHandler />
          <Nav
            onToggleSettings={() => setSettingsOpen((p) => !p)}
            settingsOpen={settingsOpen}
          />
          <SettingsSidebar
            open={settingsOpen}
            onClose={() => setSettingsOpen(false)}
          />
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />

            {/* Protected routes */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Navigate to="/live" replace />
                </ProtectedRoute>
              }
            />
            <Route
              path="/kite"
              element={
                <ProtectedRoute>
                  <KitePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/paper"
              element={
                <ProtectedRoute>
                  <PaperPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/live"
              element={
                <ProtectedRoute>
                  <LivePage />
                </ProtectedRoute>
              }
            />

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </SessionWatcherWrapper>
      </Router>
    </div>
  );
}

export default function App() {
  return (
    <SocketContext.Provider value={socket}>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </SocketContext.Provider>
  );
}